import { useEffect, useState } from "react";
import { Calendar, MessageCircle, CheckCircle2, AlertCircle, Copy, ExternalLink, KeyRound, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";
import ScheduleSettings from "@/components/ScheduleSettings";

export default function Settings() {
  const [status, setStatus] = useState(null);
  const [pinStatus, setPinStatus] = useState({ configured: false });
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [savingPin, setSavingPin] = useState(false);

  useEffect(() => {
    api.get("/integrations/status").then(({ data }) => setStatus(data));
    api.get("/auth/staff/pin/status").then(({ data }) => setPinStatus(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const savePin = async (e) => {
    e.preventDefault();
    if (newPin.length !== 6) {
      toast.error("El PIN debe tener 6 dígitos");
      return;
    }
    if (newPin !== confirmPin) {
      toast.error("Los PINs no coinciden");
      return;
    }
    setSavingPin(true);
    try {
      await api.post("/auth/staff/pin/set", { pin: newPin });
      toast.success("PIN guardado");
      setNewPin("");
      setConfirmPin("");
      setPinStatus({ configured: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar el PIN");
    } finally {
      setSavingPin(false);
    }
  };

  const reconnectGoogle = async () => {
    const { data } = await api.get("/auth/google/login");
    window.location.href = data.authorization_url;
  };

  const icsUrl = `${process.env.REACT_APP_BACKEND_URL}/api/appointments/export.ics?token=${localStorage.getItem("agenda_token")}`;

  const copyIcs = () => {
    navigator.clipboard.writeText(icsUrl);
    toast.success("URL copiada al portapapeles");
  };

  return (
    <div className="p-8 lg:p-12 max-w-5xl">
      <header className="mb-10">
        <span className="label-eyebrow text-[#B5704A]">Integraciones</span>
        <h1 className="font-display text-3xl sm:text-4xl font-[350] text-[#3A2418] mt-2">
          Tus <span className="italic font-[700]">conexiones</span>
        </h1>
        <p className="text-[#6F5A4C] mt-2 text-sm">Gestiona las integraciones externas de tu agenda</p>
      </header>

      <div className="space-y-4">
        {/* Schedule settings (hours + blocks) */}
        <ScheduleSettings />

        {/* Google Calendar */}
        <div className="surface-card p-6" data-testid="integration-google">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-md bg-[#B5704A]/10 flex items-center justify-center">
              <Calendar className="w-6 h-6 text-[#B5704A]" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-display text-lg font-[600] text-[#3A2418]">Google Calendar</h3>
                {status?.google_calendar?.connected ? (
                  <span className="inline-flex items-center gap-1 text-xs text-[#8C5A3A]"><CheckCircle2 className="w-3.5 h-3.5" /> Conectado</span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs text-[#B5704A]"><AlertCircle className="w-3.5 h-3.5" /> Desconectado</span>
                )}
              </div>
              <p className="text-sm text-[#6F5A4C] mt-1">
                Sincronización bidireccional. Las citas creadas se publican automáticamente en tu calendario primario de Google.
              </p>
              <Button onClick={reconnectGoogle} variant="outline" className="mt-4 rounded-full border-[#E8DFD2]" data-testid="reconnect-google">
                {status?.google_calendar?.connected ? "Reconectar" : "Conectar Google"}
              </Button>
            </div>
          </div>
        </div>

        {/* iCloud */}
        <div className="surface-card p-6" data-testid="integration-icloud">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-md bg-[#E8DFD2]/40 flex items-center justify-center">
              <Calendar className="w-6 h-6 text-[#6F5A4C]" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-display text-lg font-[600] text-[#3A2418]">iCloud Calendar</h3>
                <span className="inline-flex items-center gap-1 text-xs text-[#8C5A3A]"><CheckCircle2 className="w-3.5 h-3.5" /> Disponible</span>
              </div>
              <p className="text-sm text-[#6F5A4C] mt-1">
                Apple no provee una API pública. Usa la URL .ics abajo para suscribirte desde iCloud Calendar:
                <strong className="text-[#3A2418]"> Archivo → Nueva Suscripción de Calendario</strong> y pega la URL.
              </p>
              <div className="mt-4 flex items-center gap-2">
                <code className="flex-1 px-3 py-2 text-xs bg-[#F4EFE4] rounded-md border border-[#E8DFD2] truncate font-mono" data-testid="ics-url">
                  {icsUrl}
                </code>
                <Button onClick={copyIcs} variant="outline" size="sm" className="rounded-full" data-testid="copy-ics-btn">
                  <Copy className="w-3.5 h-3.5 mr-1" /> Copiar
                </Button>
                <Button onClick={() => window.open(icsUrl, "_blank")} variant="outline" size="sm" className="rounded-full" data-testid="open-ics-btn">
                  <ExternalLink className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* WhatsApp */}
        <div className="surface-card p-6" data-testid="integration-whatsapp">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-md bg-[#B5704A]/10 flex items-center justify-center">
              <MessageCircle className="w-6 h-6 text-[#B5704A]" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-display text-lg font-[600] text-[#3A2418]">WhatsApp Business (Meta)</h3>
                {status?.whatsapp?.connected ? (
                  <span className="inline-flex items-center gap-1 text-xs text-[#8C5A3A]"><CheckCircle2 className="w-3.5 h-3.5" /> Conectado</span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs text-[#B5704A]"><AlertCircle className="w-3.5 h-3.5" /> Revisar credenciales</span>
                )}
              </div>
              <p className="text-sm text-[#6F5A4C] mt-1">
                Envío de recordatorios automáticos por WhatsApp Cloud API. Phone Number ID configurado: <code className="text-xs bg-[#F4EFE4] px-1.5 py-0.5 rounded">{status?.whatsapp?.phone_number_id || "—"}</code>
              </p>
              <p className="text-xs text-[#6F5A4C] mt-3 italic">
                Las credenciales se configuran en el archivo .env del servidor. Para enviar mensajes fuera de la ventana de 24h, debes usar un template aprobado por Meta.
              </p>
            </div>
          </div>
        </div>
        {/* PIN de acceso rápido */}
        <div className="surface-card p-6" data-testid="integration-pin">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-md bg-[#B5704A]/10 flex items-center justify-center">
              <KeyRound className="w-6 h-6 text-[#B5704A]" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-display text-lg font-[600] text-[#3A2418]">Acceso por PIN</h3>
                {pinStatus.configured ? (
                  <span className="inline-flex items-center gap-1 text-xs text-[#8C5A3A]"><ShieldCheck className="w-3.5 h-3.5" /> Configurado</span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs text-[#B5704A]"><AlertCircle className="w-3.5 h-3.5" /> Sin configurar</span>
                )}
              </div>
              <p className="text-sm text-[#6F5A4C] mt-1">
                Define un PIN de 6 dígitos para entrar al panel de forma rápida desde tu celular, sin esperar el enlace por correo.
              </p>
              <form onSubmit={savePin} className="mt-4 grid sm:grid-cols-2 gap-3 max-w-md" data-testid="pin-set-form">
                <div>
                  <Label className="text-xs text-[#6F5A4C]">Nuevo PIN</Label>
                  <Input
                    type="password"
                    inputMode="numeric"
                    pattern="\d{6}"
                    maxLength={6}
                    required
                    value={newPin}
                    onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ""))}
                    placeholder="••••••"
                    className="mt-1 h-10 bg-white border-[#E8DFD2] text-center tracking-[0.4em]"
                    data-testid="new-pin-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#6F5A4C]">Confirmar PIN</Label>
                  <Input
                    type="password"
                    inputMode="numeric"
                    pattern="\d{6}"
                    maxLength={6}
                    required
                    value={confirmPin}
                    onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ""))}
                    placeholder="••••••"
                    className="mt-1 h-10 bg-white border-[#E8DFD2] text-center tracking-[0.4em]"
                    data-testid="confirm-pin-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={savingPin}
                  className="sm:col-span-2 rounded-full bg-[#3A2418] hover:bg-[#3A2418]/90 text-white h-10 mt-2"
                  data-testid="save-pin-button"
                >
                  {savingPin ? "Guardando…" : pinStatus.configured ? "Cambiar PIN" : "Configurar PIN"}
                </Button>
              </form>
              <p className="text-xs text-[#6F5A4C] mt-3 italic">
                Tras 5 intentos fallidos, el acceso por PIN se bloquea 15 minutos por seguridad.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
